<template>
  <div class="gpc-mapper">
    <!-- System Status Alert -->
    <v-alert
      v-if="systemStatus.type !== 'success'"
      :type="systemStatus.type"
      class="mb-4"
      dismissible
    >
      {{ systemStatus.message }}
      
      <!-- Upload button for admin when no data -->
      <template v-if="systemStatus.type === 'warning' && systemStatus.showUpload">
        <div class="mt-2">
          <v-file-input
            accept=".json"
            label="Upload GPC Bricks Data (JSON)"
            @change="onGpcDataUpload"
            class="mt-2"
            style="max-width: 300px;"
          />
        </div>
      </template>
    </v-alert>

    <!-- View Switcher -->
    <div class="view-switcher mb-4">
      <v-btn-toggle v-model="activeView" mandatory>
        <v-btn value="mapping" :disabled="!isSystemReady">
          <v-icon left>mdi-link</v-icon>
          Mapping View
        </v-btn>
        <v-btn value="bricks" :disabled="!isSystemReady">
          <v-icon left>mdi-view-grid</v-icon>
          Bricks View
        </v-btn>
      </v-btn-toggle>
    </div>

    <!-- Loading State -->
    <div v-if="loading" class="text-center py-8">
      <v-progress-circular indeterminate size="64" />
      <p class="mt-4 text-grey">Loading GPC data...</p>
    </div>

    <!-- Mapping View -->
    <div v-else-if="activeView === 'mapping' && isSystemReady">
      <div class="d-flex justify-space-between align-center mb-4">
        <h3>Backend Category to GPC Mapping ({{ mappings.length }} mappings)</h3>
        <div class="d-flex gap-2 align-center">
          <!-- GPC Data Management -->
          <v-btn color="grey" @click="$refs.gpcDataFileInput.click()" variant="outlined" size="small">
            <v-icon left>mdi-database-refresh</v-icon>
            Update GPC Data
          </v-btn>
          <input
            ref="gpcDataFileInput"
            type="file"
            accept=".json"
            style="display: none;"
            @change="onGpcDataUpload"
          />
          
          <v-divider vertical class="mx-1"></v-divider>
          
          <!-- Data Actions -->
          <v-btn 
  color="success" 
  @click="handleSave" 
  :disabled="mappings.length === 0"
>
  <v-icon left>mdi-content-save</v-icon>
  Save
</v-btn>
<v-btn 
  color="purple" 
  @click="openPublishDialog" 
  :disabled="mappings.length === 0"
  variant="elevated"
>
  <v-icon left>mdi-publish</v-icon>
  Publish
</v-btn>

<!-- Add the Publish Dialog at the end of template -->
<v-dialog v-model="showPublishDialog" max-width="500px">
  <v-card>
    <v-card-title class="headline">
      <v-icon left color="purple">mdi-publish</v-icon>
      Publish GPC Mappings
    </v-card-title>
    
    <v-card-text>
      <v-alert type="info" variant="tonal" class="mb-4">
        This will publish the current Working Version to the selected environment.
      </v-alert>
      
      <p class="mb-4">
        <strong>Current mappings:</strong> {{ mappings.length }} mapping{{ mappings.length !== 1 ? 's' : '' }}
      </p>
      
      <v-radio-group v-model="publishTarget" label="Select target environment:">
        <v-radio label="Production" value="production" color="green">
          <template v-slot:label>
            <div>
              <strong>Production</strong>
              <div class="text-caption text-grey">Live environment used by all systems</div>
            </div>
          </template>
        </v-radio>
        <v-radio label="Test" value="test" color="orange">
          <template v-slot:label>
            <div>
              <strong>Test</strong>
              <div class="text-caption text-grey">Testing environment for validation</div>
            </div>
          </template>
        </v-radio>
      </v-radio-group>
      
      <v-checkbox
        v-model="createBackup"
        label="Create backup of existing version"
        color="primary"
        class="mt-4"
      />
    </v-card-text>
    
    <v-card-actions>
      <v-spacer />
      <v-btn @click="closePublishDialog" variant="text">Cancel</v-btn>
      <v-btn 
        color="purple" 
        @click="confirmPublish" 
        :disabled="!publishTarget"
        :loading="publishLoading"
        variant="elevated"
      >
        Publish to {{ publishTarget }}
      </v-btn>
    </v-card-actions>
  </v-card>
</v-dialog>

<!-- Add a status indicator in the header -->
<v-chip 
  v-if="props.lastPublishInfo" 
  class="ml-2"
  color="success"
  variant="outlined"
  size="small"
>
  <v-icon left size="small">mdi-check-circle</v-icon>
  Last published: {{ props.lastPublishInfo.target }} 
  ({{ new Date(props.lastPublishInfo.publishedAt).toLocaleString() }})
</v-chip>
          <v-btn color="info" @click="$emit('export-mappings')" :disabled="mappings.length === 0">
            <v-icon left>mdi-download</v-icon>
            Export CSV
          </v-btn>

          <!-- Import Actions -->
          <v-btn color="orange" @click="$refs.csvFileInput.click()" variant="outlined">
            <v-icon left>mdi-upload</v-icon>
            Import CSV
          </v-btn>
          <input
            ref="csvFileInput"
            type="file"
            accept=".csv"
            style="display: none;"
            @change="onFileImport"
          />
          
          <!-- Create Actions -->
          <v-divider vertical class="mx-2"></v-divider>
          <v-btn color="primary" @click="addNewMapping">
            <v-icon left>mdi-plus</v-icon>
            Add New
          </v-btn>
        </div>
      </div>

      <!-- Empty State for Mappings -->
      <v-card v-if="mappings.length === 0" class="text-center py-8">
        <v-card-text>
          <v-icon size="64" color="grey-lighten-1">mdi-link-off</v-icon>
          <h3 class="mt-4 mb-2">No GPC Mappings Yet</h3>
          <p class="text-grey mb-4">Create your first mapping between backend categories and GPC bricks</p>
          <v-btn color="primary" @click="addNewMapping">
            <v-icon left>mdi-plus</v-icon>
            Create First Mapping
          </v-btn>
        </v-card-text>
      </v-card>

      <!-- Mappings Table -->
      <v-card v-else>
        <v-data-table
          :headers="mappingHeaders"
          :items="mappings"
          :items-per-page="10"
          class="elevation-1"
        >
          <template #item.backendCategoryId="{ item }">
            <div class="mapping-cell">
              <v-chip
                v-if="item.backendCategoryId"
                closable
                @click:close="removeBackendCategory(item)"
                class="ma-1"
              >
                {{ getCategoryDisplayPath(item.backendCategoryId) }}
              </v-chip>
              <v-btn
                v-else
                variant="text"
                size="small"
                color="primary"
                @click="openAddCategory(item)"
                :disabled="backendCategoriesData.length === 0"
              >
                <v-icon left>mdi-plus</v-icon>
                Add Category
              </v-btn>
            </div>
          </template>

          <template #item.gpcBricks="{ item }">
            <div class="mapping-cell">
              <v-chip
                v-for="(brick, index) in item.gpcBricks"
                :key="`${item.id}-brick-${brick}-${index}`"
                closable
                @click:close="removeBrick(item, index)"
                class="ma-1"
              >
                {{ brick }}
              </v-chip>
              <v-btn
                variant="text"
                size="small"
                color="primary"
                @click="openAddBrick(item)"
                :disabled="gpcBricksData.length === 0"
              >
                <v-icon left>mdi-plus</v-icon>
                Add Brick
              </v-btn>
            </div>
          </template>

          <template #item.actions="{ item }">
            <v-btn
              icon
              size="small"
              color="error"
              @click="removeMapping(item)"
            >
              <v-icon>mdi-delete</v-icon>
            </v-btn>
          </template>
        </v-data-table>
      </v-card>
    </div>

    <!-- Bricks View -->
    <div v-else-if="activeView === 'bricks' && isSystemReady">
      <div class="d-flex justify-space-between align-center mb-4">
        <h3>GPC Bricks Breakdown ({{ filteredBricks.length }} bricks)</h3>
        
        <!-- GPC Data Management -->
        <div class="d-flex gap-2 align-center">
          <v-btn color="orange" @click="$refs.gpcFileInput.click()" variant="outlined" size="small">
            <v-icon left>mdi-database-refresh</v-icon>
            Update GPC Data
          </v-btn>
          <input
            ref="gpcFileInput"
            type="file"
            accept=".json"
            style="display: none;"
            @change="onGpcDataUpload"
          />
        </div>
      </div>

      <v-text-field
        v-model="brickFilter"
        label="Filter bricks..."
        prepend-inner-icon="mdi-magnify"
        clearable
        class="mb-4"
      />

      <!-- Empty State for filtered bricks -->
      <v-card v-if="filteredBricks.length === 0" class="text-center py-8">
        <v-card-text>
          <v-icon size="64" color="grey-lighten-1">mdi-cube-outline</v-icon>
          <h3 class="mt-4 mb-2">No Bricks Found</h3>
          <p class="text-grey">
            {{ brickFilter ? 'No bricks match your search criteria' : 'No GPC bricks data available' }}
          </p>
          <v-btn v-if="brickFilter" color="primary" @click="brickFilter = ''">
            Clear Filter
          </v-btn>
        </v-card-text>
      </v-card>

      <div class="brick-cards" v-else>
        <v-card
          v-for="brick in filteredBricks"
          :key="brick.code"
          class="mb-4"
        >
          <v-card-title class="bg-grey-lighten-4">
            {{ brick.code }} - {{ brick.title }}
          </v-card-title>
          <v-card-text>
            <v-row>
              <v-col cols="12">
                <strong>Full Path:</strong> {{ brick.fullPath }}
              </v-col>
              <v-col cols="3">
                <strong>Segment:</strong> {{ brick.segment }}
              </v-col>
              <v-col cols="3">
                <strong>Family:</strong> {{ brick.family }}
              </v-col>
              <v-col cols="3">
                <strong>Class:</strong> {{ brick.class }}
              </v-col>
            </v-row>
            
            <v-divider class="my-3" />
            
            <div>
              <h6 class="mb-2">Mapped Backend Categories:</h6>
              <div v-if="brick.mappedCategories && brick.mappedCategories.length > 0">
                <v-chip
                  v-for="category in brick.mappedCategories"
                  :key="category"
                  class="ma-1"
                  size="small"
                >
                  {{ getCategoryDisplayPath(category) }}
                </v-chip>
              </div>
              <div v-else class="text-grey">
                No backend categories mapped
              </div>
            </div>
          </v-card-text>
        </v-card>
      </div>
    </div>

    <!-- System Not Ready State -->
    <div v-else class="text-center py-8">
      <v-icon size="64" color="grey-lighten-1">mdi-alert-circle</v-icon>
      <h3 class="mt-4 mb-2">GPC System Not Ready</h3>
      <p class="text-grey">Please ensure GPC bricks data is loaded before using mapping features.</p>
    </div>

    <!-- Search Modal -->
    <v-dialog v-model="showSearchModal" max-width="600px">
      <v-card>
        <v-card-title>
          {{ searchModalType === 'category' ? 'Add Backend Category' : 'Add GPC Brick' }}
        </v-card-title>
        <v-card-text>
          <v-text-field
            v-model="searchQuery"
            label="Type to search..."
            prepend-inner-icon="mdi-magnify"
            @input="performSearch"
            autofocus
          />

          <!-- Info about unmapped categories -->
          <p v-if="searchModalType === 'category'" class="text-caption text-grey mb-2">
            Only showing unmapped categories (each category can only be mapped once)
          </p>
          
          <v-list max-height="300" class="overflow-y-auto">
            <v-list-item
              v-for="result in searchResults"
              :key="result.id || result"
              @click="selectSearchResult(result)"
            >
              <v-list-item-title>{{ result.display || result }}</v-list-item-title>
            </v-list-item>
            <v-list-item v-if="searchResults.length === 0">
              <v-list-item-title class="text-grey">
                {{ getEmptySearchMessage() }}
              </v-list-item-title>
            </v-list-item>
          </v-list>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn @click="closeSearchModal">Cancel</v-btn>
          <v-btn color="primary" @click="addSearchResult" :disabled="!searchQuery.trim()">Add</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Add New Mapping Dialog -->
    <v-dialog v-model="showAddMappingDialog" max-width="700px" persistent>
      <v-card>
        <v-card-title>
          <v-icon left color="primary">mdi-link-plus</v-icon>
          Add New GPC Mapping
        </v-card-title>
        <v-card-text>
          <!-- Backend Category Selection -->
          <div class="mb-4">
            <label class="text-subtitle-2 font-weight-medium mb-2 d-block">Backend Category *</label>
            <v-autocomplete
              v-model="newMappingCategory"
              :items="availableCategoriesForNewMapping"
              item-title="display"
              item-value="id"
              label="Select a backend category"
              placeholder="Search categories..."
              clearable
              :no-data-text="availableCategoriesForNewMapping.length === 0 ? 'All categories already mapped' : 'No matching categories'"
            />
            <p class="text-caption text-grey mt-1">
              {{ availableCategoriesForNewMapping.length }} unmapped categories available
            </p>
          </div>

          <!-- GPC Bricks Selection -->
          <div class="mb-4">
            <label class="text-subtitle-2 font-weight-medium mb-2 d-block">GPC Bricks</label>
            <v-autocomplete
              v-model="newMappingBricks"
              :items="gpcBrickOptions"
              item-title="display"
              item-value="display"
              label="Select GPC bricks"
              placeholder="Search bricks..."
              multiple
              chips
              closable-chips
              clearable
            />
          </div>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn @click="closeAddMappingDialog" variant="text">Cancel</v-btn>
          <v-btn 
            color="primary" 
            @click="confirmAddMapping" 
            :disabled="!newMappingCategory"
          >
            Add Mapping
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, watch, nextTick } from 'vue'

// Props
const props = defineProps({
  mappings: { type: Array, default: () => [] },
  gpcBricksData: { type: Array, default: () => [] },
  backendCategoriesData: { type: Array, default: () => [] },
  backendTaxonomyData: { type: Object, default: () => ({}) },
  loading: { type: Boolean, default: false },
  systemStatus: { type: Object, default: () => ({ type: 'success', message: '' }) }
})

// Emits
const emit = defineEmits([
  'mappingChanged', 'saveRequired', 'save-mappings', 
  'export-mappings', 'import-mappings', 'upload-gpc-data', 'publish-mappings'
])

// Reactive data
const activeView = ref('mapping')
const showSearchModal = ref(false)
const searchModalType = ref('')
const searchQuery = ref('')
const searchResults = ref([])
const currentMappingItem = ref(null)
const brickFilter = ref('')
const showOnlyUnmapped = ref(false)
const categoryDisplayInfo = ref({})
const showPublishDialog = ref(false)
const publishTarget = ref('')
const createBackup = ref(true)
const publishLoading = ref(false)
const lastPublishInfo = ref('')

// Add New Mapping Dialog
const showAddMappingDialog = ref(false)
const newMappingCategory = ref(null)
const newMappingBricks = ref([])

// Computed
const mappingHeaders = [
  { title: 'Backend Category', key: 'backendCategoryId', width: '40%' },
  { title: 'GPC Bricks', key: 'gpcBricks', width: '50%' },
  { title: 'Actions', key: 'actions', width: '10%', sortable: false }
]

const isSystemReady = computed(() => {
  return props.gpcBricksData.length > 0 && !props.loading
})

const mappedBackendCategories = computed(() => {
  const currentEditingId = currentMappingItem.value?.id
  const mapped = props.mappings
    .filter(m => m.id !== currentEditingId)
    .map(m => m.backendCategoryId)
    .filter(Boolean)
  return new Set(mapped)
})

const filteredBricks = computed(() => {
  if (!brickFilter.value) return props.gpcBricksData
  const filter = brickFilter.value.toLowerCase()
  return props.gpcBricksData.filter(brick => 
    brick.code.toLowerCase().includes(filter) ||
    brick.title.toLowerCase().includes(filter) ||
    brick.segment.toLowerCase().includes(filter) ||
    brick.family.toLowerCase().includes(filter) ||
    brick.class.toLowerCase().includes(filter) ||
    (brick.fullPath && brick.fullPath.toLowerCase().includes(filter))
  )
})

// Available categories for new mapping (excludes already mapped ones)
const availableCategoriesForNewMapping = computed(() => {
  // Get all already mapped category IDs
  const mappedIds = new Set(
    props.mappings
      .map(m => m.backendCategoryId)
      .filter(Boolean)
  )
  
  // Return categories that are not yet mapped
  return Object.entries(categoryDisplayInfo.value)
    .filter(([id]) => !mappedIds.has(id))
    .map(([id, info]) => ({
      id,
      display: info.path || id
    }))
    .sort((a, b) => a.display.localeCompare(b.display))
})

// GPC brick options for the dropdown
const gpcBrickOptions = computed(() => {
  return props.gpcBricksData.map(brick => ({
    id: brick.code,
    display: `${brick.code} - ${brick.title}`
  }))
})

// Load category display info
function loadCategoryDisplayInfo() {
  if (props.backendTaxonomyData) {
    const displayInfo = {}
    
    function traverse(node, currentPath = []) {
      if (!node || !node.name) return
      
      const newPath = [...currentPath, node.name]
      
      if (node.id) {
        displayInfo[node.id] = {
          name: node.name,
          path: newPath.join(' > ')
        }
      }
      
      if (Array.isArray(node.subcategories)) {
        node.subcategories.forEach(sub => traverse(sub, newPath))
      }
    }
    
    Object.values(props.backendTaxonomyData).forEach(topLevelNode => {
      traverse(topLevelNode, [])
    })
    
    categoryDisplayInfo.value = displayInfo
    console.log('[GPC Mapper] Loaded', Object.keys(displayInfo).length, 'categories')
  }
}

onMounted(() => {
  if (props.backendCategoriesData && props.backendTaxonomyData) {
    loadCategoryDisplayInfo()
  }
})

watch(() => props.backendTaxonomyData, (newData) => {
  console.log('[GPC Mapper] Backend taxonomy data changed')
  if (newData) {
    loadCategoryDisplayInfo()
  }
}, { immediate: true })

function openPublishDialog() {
  // Check parent's hasUnsavedChanges prop
  if (props.hasUnsavedChanges) {
    // The parent might handle this warning, or you can show it here
    if (!confirm('You have unsaved changes. They will be included in the publish. Continue?')) {
      return
    }
  }
  
  publishTarget.value = ''
  createBackup.value = true
  showPublishDialog.value = true
}

function closePublishDialog() {
  showPublishDialog.value = false
  publishTarget.value = ''
}

function confirmPublish() {
  if (!publishTarget.value) return
  
  publishLoading.value = true
  
  // Just emit the event - parent handles the async operation
  emit('publish-mappings', {
    target: publishTarget.value,
    createBackup: createBackup.value,
    mappings: props.mappings
  })
  
  // Close dialog after emitting
  setTimeout(() => {
    publishLoading.value = false
    closePublishDialog()
    // Update last publish info optimistically
    lastPublishInfo.value = `${publishTarget.value} (${new Date().toLocaleString()})`
  }, 500)
}

// Update handleSave to reset the unsaved flag
function handleSave() {
  // Recalculate all paths before saving
  recalculateAllPaths()
  
  // Just emit - don't manage hasUnsavedChanges here
  nextTick(() => {
    emit('save-mappings')
  })
}

function getCategoryDisplayPath(categoryId) {
  if (categoryId && categoryId.includes(' > ')) {
    return categoryId
  }
  
  function findInTaxonomy(node, targetId, currentPath = []) {
    if (!node || !node.name) return null
    const newPath = [...currentPath, node.name]
    if (node.id === targetId) {
      return newPath.join(' > ')
    }
    if (Array.isArray(node.subcategories)) {
      for (const sub of node.subcategories) {
        const result = findInTaxonomy(sub, targetId, newPath)
        if (result) return result
      }
    }
    return null
  }
  
  for (const topLevel of Object.values(props.backendTaxonomyData || {})) {
    const path = findInTaxonomy(topLevel, categoryId, [])
    if (path) return path
  }
  
  return categoryId || '(unknown)'
}

function recalculateAllPaths() {
  console.log('[GPC Mapper] Recalculating all paths...')
  
  // Create deep copy of all mappings
  const updatedMappings = JSON.parse(JSON.stringify(props.mappings))
  
  // Update every mapping that has a backendCategoryId
  updatedMappings.forEach(mapping => {
    if (mapping.backendCategoryId) {
      const oldPath = mapping.backendCategory
      const freshPath = getCategoryDisplayPath(mapping.backendCategoryId)
      mapping.backendCategory = freshPath.replace(/ > /g, '|')
      
      if (oldPath !== mapping.backendCategory) {
        console.log('[GPC Mapper] Path updated for mapping:', {
          id: mapping.id,
          categoryId: mapping.backendCategoryId,
          oldPath: oldPath,
          newPath: mapping.backendCategory
        })
      }
    }
  })
  
  // Emit the updated array with all fresh paths
  emit('mappingChanged', updatedMappings)
  
  return updatedMappings
}
// Update the existing performSearch function
function performSearch() {
  const query = searchQuery.value.toLowerCase()
  
  if (searchModalType.value === 'category') {
    // First, ensure we have display info loaded
    if (Object.keys(categoryDisplayInfo.value).length === 0) {
      console.warn('[GPC Mapper] No category display info available. Attempting to reload...')
      loadCategoryDisplayInfo()
      if (Object.keys(categoryDisplayInfo.value).length === 0) {
        searchResults.value = []
        return
      }
    }
    
    // Use ALL IDs from categoryDisplayInfo
    // Don't filter by backendCategoriesData since those IDs don't match
    let availableIds = Object.keys(categoryDisplayInfo.value)
    
    // ALWAYS filter out already-mapped categories (each category can only be mapped once)
    availableIds = availableIds.filter(categoryId => {
      return !mappedBackendCategories.value.has(categoryId)
    })
    
    // If no query, show first 50 available categories
    if (!query || query.length === 0) {
      searchResults.value = availableIds
        .slice(0, 50)
        .map(id => ({
          id: id,
          display: categoryDisplayInfo.value[id]?.path || id
        }))
      
      console.log('[GPC Mapper] Showing initial categories:', {
        totalAvailable: availableIds.length,
        showing: searchResults.value.length,
        firstFew: searchResults.value.slice(0, 3).map(r => r.display)
      })
    } else {
      // Filter based on search query
      searchResults.value = availableIds
        .filter(id => {
          const info = categoryDisplayInfo.value[id]
          if (!info) return false
          const pathLower = (info.path || '').toLowerCase()
          const nameLower = (info.name || '').toLowerCase()
          return pathLower.includes(query) || nameLower.includes(query)
        })
        .map(id => ({
          id: id,
          display: categoryDisplayInfo.value[id]?.path || id
        }))
        .slice(0, 100) // Limit results for performance
    }
    
    // Add debug logging if no results found
    if (searchResults.value.length === 0 && searchModalType.value === 'category') {
      console.warn('[GPC Mapper] No category search results. Debug info:', {
        query: query,
        categoryDisplayInfoCount: Object.keys(categoryDisplayInfo.value).length,
        availableIdsCount: availableIds ? availableIds.length : 0,
        sampleDisplayInfo: Object.entries(categoryDisplayInfo.value).slice(0, 3)
      })
    }
  } else {
    // GPC brick search - DON'T include fullPath in the saved value
    searchResults.value = props.gpcBricksData
      .filter(brick => 
        brick.code.toLowerCase().includes(query) ||
        brick.title.toLowerCase().includes(query) ||
        brick.segment.toLowerCase().includes(query) ||
        brick.family.toLowerCase().includes(query) ||
        brick.class.toLowerCase().includes(query) ||
        (brick.fullPath && brick.fullPath.toLowerCase().includes(query))
      )
      .map(brick => ({
        id: brick.code,
        display: `${brick.code} - ${brick.title}`  // <-- Remove the fullPath from here!
        // You can still show it in the UI if needed, but don't save it
      }))
  }
}

function onFileImport(event) {
  const file = event.target.files?.[0];
  if (!file) return;

  const reader = new FileReader();
  reader.onload = (e) => {
    try {
      const lines = e.target.result.split(/\r?\n/).filter(line => line.trim());
      if (lines.length <= 1) { 
        emit('import-mappings', []); 
        return; 
      }
      
      const parsedMappings = lines.slice(1).map((line, index) => {
        const columns = parseCsvLine(line);
        const backendCategoryValue = columns[0] || '';
        
        let categoryId = '';
        let categoryPath = '';
        
        if (backendCategoryValue.startsWith('be_id_')) {
          categoryId = backendCategoryValue;
          categoryPath = getCategoryDisplayPath(categoryId).replace(/ > /g, '|');
        } else if (backendCategoryValue.includes('|') || backendCategoryValue.includes(' > ')) {
          categoryPath = backendCategoryValue.replace(/ > /g, '|');
          const matchingId = Object.keys(categoryDisplayInfo.value).find(id => {
            const info = categoryDisplayInfo.value[id];
            return info && info.path.replace(/ > /g, '|') === categoryPath;
          });
          categoryId = matchingId || '';
        }
        
        return {
          id: index + 1, // Simple incrementing ID
          backendCategoryId: categoryId,
          backendCategory: categoryPath,
          gpcBricks: columns.slice(1).filter(Boolean)
        };
      });
      
      emit('import-mappings', parsedMappings);
      // Parent will show success message
      
    } catch (error) { 
      console.error('Failed to parse CSV:', error);
      // Parent should handle error notification
    }
  };
  reader.readAsText(file);
  event.target.value = '';
}

function addNewMapping() {
  // Open the dialog instead of adding an empty row
  newMappingCategory.value = null
  newMappingBricks.value = []
  showAddMappingDialog.value = true
}

function closeAddMappingDialog() {
  showAddMappingDialog.value = false
  newMappingCategory.value = null
  newMappingBricks.value = []
}

function confirmAddMapping() {
  if (!newMappingCategory.value) return
  
  // Check if category is already mapped (shouldn't happen with filtered list, but double-check)
  const alreadyMapped = props.mappings.some(m => m.backendCategoryId === newMappingCategory.value)
  if (alreadyMapped) {
    console.warn('Category already mapped')
    return
  }
  
  const newId = Math.max(0, ...props.mappings.map(m => m.id || 0)) + 1
  const categoryPath = getCategoryDisplayPath(newMappingCategory.value)
  
  const newMapping = { 
    id: newId, 
    backendCategoryId: newMappingCategory.value,
    backendCategory: categoryPath.replace(/ > /g, '|'),
    gpcBricks: newMappingBricks.value || []
  }
  
  emit('mappingChanged', [...props.mappings, newMapping])
  closeAddMappingDialog()
}

function removeMapping(item) {
  emit('mappingChanged', props.mappings.filter(m => m.id !== item.id))
}

function removeBackendCategory(item) {
  delete item.backendCategoryId
  delete item.backendCategory  // Also remove the path string
  emit('mappingChanged', props.mappings)
}

function removeBrick(item, brickIndex) {
  // Create a new array without the removed brick (better for reactivity)
  const updatedBricks = [...item.gpcBricks]
  updatedBricks.splice(brickIndex, 1)
  
  // Create updated mappings with the new bricks array
  const updatedMappings = props.mappings.map(m => {
    if (m.id === item.id) {
      return { ...m, gpcBricks: updatedBricks }
    }
    return m
  })
  
  emit('mappingChanged', updatedMappings)
}

function openAddCategory(item) {
  if (props.backendCategoriesData.length === 0) {
    console.warn('[GPC Mapper] No backend categories available')
    return
  }
  
  console.log('[GPC Mapper] Opening category search with:', {
    backendCategoriesCount: props.backendCategoriesData.length,
    displayInfoCount: Object.keys(categoryDisplayInfo.value).length,
    sampleIds: props.backendCategoriesData.slice(0, 5)
  })
  
  currentMappingItem.value = item
  searchModalType.value = 'category'
  searchQuery.value = ''
  showOnlyUnmapped.value = false
  
  // Immediately populate some results
  performSearch()
  
  // Debug: Log search results
  console.log('[GPC Mapper] Initial search results:', searchResults.value.length)
  
  showSearchModal.value = true
}

function openAddBrick(item) {
  if (props.gpcBricksData.length === 0) return
  currentMappingItem.value = item
  searchModalType.value = 'brick'
  searchQuery.value = ''
  performSearch()
  showSearchModal.value = true
}

function closeSearchModal() {
  showSearchModal.value = false
  searchQuery.value = ''
  searchResults.value = []
  currentMappingItem.value = null
}

function selectSearchResult(result) {
  searchQuery.value = result.display || result
}

function addSearchResult() {
  if (!searchQuery.value.trim() || !currentMappingItem.value) return
  
  if (searchModalType.value === 'category') {
    const selectedResult = searchResults.value.find(r => r.display === searchQuery.value)
    if (selectedResult) {
      // Just set the ID - don't worry about the path here
      currentMappingItem.value.backendCategoryId = selectedResult.id
      // Optionally set a temporary path
      currentMappingItem.value.backendCategory = selectedResult.display.replace(/ > /g, '|')
    }
  } else {
    const selectedResult = searchResults.value.find(r => r.display === searchQuery.value)
    if (selectedResult && !currentMappingItem.value.gpcBricks.includes(selectedResult.display)) {
      currentMappingItem.value.gpcBricks.push(selectedResult.display)
    }
  }
  
  emit('mappingChanged', props.mappings)
  closeSearchModal()
}

function parseCsvLine(line) {
  const columns = [];
  const regex = /(?:"([^"]*(?:""[^"]*)*)"|([^,]*))(?:,|$)/g;
  let match;
  while ((match = regex.exec(line))) {
    const value = match[1] !== undefined ? match[1].replace(/""/g, '"') : match[2];
    columns.push(value.trim());
    if (match[0].slice(-1) !== ',') break;
  }
  return columns;
}



function onGpcDataUpload(event) {
  const file = event.target.files?.[0]
  if (file) emit('upload-gpc-data', file)
  event.target.value = ''
}

function getEmptySearchMessage() {
  if (searchModalType.value === 'category') {
    return props.backendCategoriesData.length === 0 ? 'No backend categories available.' : 'No matching categories found.'
  }
  return props.gpcBricksData.length === 0 ? 'No GPC bricks available.' : 'No matching bricks found.'
}

</script>

<style scoped>
.gpc-mapper { height: 100%; display: flex; flex-direction: column; }
.mapping-cell { min-height: 48px; display: flex; flex-wrap: wrap; align-items: center; gap: 4px; }
.brick-cards { max-height: calc(100vh - 200px); overflow-y: auto; }
.view-switcher { position: sticky; top: 0; background: white; z-index: 1; padding-bottom: 16px; }
</style>